import { createFileRoute, Navigate } from "@tanstack/react-router"
import { Loader2, Play } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { PageHeader, Panel, Section, SectionStack } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { SettingRow, SettingRows } from "@/features/integration-apps/SettingRow"
import { useRefreshBrazeProfile, useTriggerCron } from "@/queries/marketplace"

export const Route = createFileRoute("/dev-tools/")({
	component: DevToolsPage,
})

interface CronAction {
	text: string
}

interface CronHandler {
	id:
		| "process-order-lifecycle"
		| "process-reminders"
		| "process-kyc-reminders"
		| "process-seller-inactivity"
		| "process-seller-promotions"
		| "process-risk-assessment"
		| "process-stuck-fund-operations"
		| "process-dispute-lifecycle"
		| "process-expired-reviews"
	title: string
	schedule: string
	summary: string
	actions: CronAction[]
}

const CRON_HANDLERS: CronHandler[] = [
	{
		id: "process-order-lifecycle",
		title: "Process Order Lifecycle",
		schedule: "Every hour",
		summary: "Checks all orders and auto-advances stuck lifecycle states.",
		actions: [
			{
				text: "Auto-cancels orders stuck in 'pending shipment' for more than 5 days — seller didn't ship, buyer gets refunded.",
			},
			{
				text: "Auto-pauses seller account when an order is auto-cancelled with no messages exchanged between buyer and seller.",
			},
			{
				text: "Auto-accepts buyer cancellation requests where the seller hasn't responded within 48 hours.",
			},
			{
				text: "Auto-releases funds to sellers for delivered orders that have passed the hold period.",
			},
		],
	},
	{
		id: "process-reminders",
		title: "Send Order Reminders",
		schedule: "Every 2 hours",
		summary:
			"Sends notification reminders to prevent orders from silently timing out.",
		actions: [
			{
				text: "Ship reminder to sellers ~4 days after order creation — nudges them before the 5-day auto-cancel.",
			},
			{
				text: "Extends auto-cancel deadline by 5 business days when both buyer and seller have exchanged messages and the deadline is within 2 days.",
			},
			{
				text: "Confirm receipt reminder to buyers ~4 days after delivery.",
			},
			{
				text: "Final confirm receipt reminder to buyers ~6 days after delivery — last warning before auto-completion.",
			},
		],
	},
	{
		id: "process-kyc-reminders",
		title: "KYC Reminders & Enforcement",
		schedule: "Every 2 hours",
		summary:
			"Sends KYC reminder notifications to individual sellers who haven't completed identity verification after their first sale.",
		actions: [
			{
				text: "Sends a first KYC reminder notification after the seller's first sale.",
			},
			{
				text: "Escalates with a final reminder after 7 days of inaction.",
			},
			{
				text: "Auto-suspends sellers who haven't completed KYC within 14 days.",
			},
		],
	},
	{
		id: "process-seller-inactivity",
		title: "Seller Inactivity Sweep",
		schedule: "Daily",
		summary:
			"Pauses sellers who had no activity for 30 + 10 days and feeds the Braze reminder pipeline.",
		actions: [
			{
				text: "Pauses sellers with live listings with no authenticated web, mobile or public-API activity for 40+ days (never before the configured go-live date) and sends the 'account paused' notification.",
			},
			{
				text: "Refreshes the Braze seller_days_until_pause attribute for sellers inside the 10-day reminder window — Braze sends the D-10/5/3/1 reminders.",
			},
		],
	},
	{
		id: "process-seller-promotions",
		title: "Seller Promotions Check",
		schedule: "Every hour",
		summary:
			"Keeps seller promotions up to date: adds sellers who were missed and sends Braze the promotion events it has not received yet.",
		actions: [
			{
				text: "Adds sellers who connected a promotion's app but were missed, including sellers already connected when the promotion started.",
			},
			{
				text: "Tells Braze when a seller's promotion reaches its end date, and sends again the 'promotion started' and 'promotion ended' events Braze did not receive.",
			},
			{
				text: "For promotions open to all sellers: tells the sellers whose fee it lowers when it starts and when it ends, and updates their Braze profiles after the promotion is edited.",
			},
		],
	},
	{
		id: "process-risk-assessment",
		title: "Assess Order Risk",
		schedule: "Every 15 min",
		summary:
			"Runs risk assessment on orders that haven't been assessed yet — catches orders that were missed by the real-time subscriber.",
		actions: [
			{
				text: "Scans all orders where riskAssessedAt is null and createdAt is older than 10 minutes.",
			},
			{
				text: "Runs all fraud detectors (high value, new buyer, IP/card/country mismatches, instant completion, etc.) and assigns a risk level.",
			},
			{
				text: "Emits order.risk_updated events for any orders where risk indicators are found.",
			},
		],
	},
	{
		id: "process-stuck-fund-operations",
		title: "Retry Failed Payments",
		schedule: "Every 10 min",
		summary:
			"Retries payment operations that previously failed due to network issues or Stripe errors.",
		actions: [
			{
				text: "Retries refunds that didn't go through (buyer should have been refunded but Stripe call failed).",
			},
			{
				text: "Retries fund releases to sellers that got stuck (order completed but transfer failed).",
			},
		],
	},
	{
		id: "process-dispute-lifecycle",
		title: "Process Dispute Deadlines",
		schedule: "Every 2 hours",
		summary:
			"Auto-escalates disputes when the seller hasn't responded in time.",
		actions: [
			{
				text: "If seller doesn't respond within 2 business days, the dispute is escalated to the CardNexus admin team.",
			},
		],
	},
	{
		id: "process-expired-reviews",
		title: "Complete Expired Reviews",
		schedule: "Every hour",
		summary:
			"Closes the review window for buyers who haven't left a review in time.",
		actions: [
			{
				text: "Auto-completes buyer reviews that have been pending for more than 7 days after order completion.",
			},
		],
	},
]

function DevToolsPage() {
	if (
		typeof window !== "undefined" &&
		window.location.hostname === "admin.cardnexus.com"
	) {
		return <Navigate to="/" />
	}

	return (
		<div className="flex max-w-5xl flex-col gap-6 p-6">
			<PageHeader
				title="Dev tools"
				description="Testing helpers for QA and development. Trigger scheduled background jobs manually to test time-dependent flows."
			/>

			<SectionStack>
				<BrazeProfileRefreshCard />

				<Section label="Background jobs">
					<p className="-mt-1 text-xs text-meta">
						These jobs run on a schedule via EventBridge; Trigger now runs one
						immediately. Each job processes all eligible items, not just a
						single order.
					</p>
					<Panel className="divide-y">
						{CRON_HANDLERS.map((handler) => (
							<CronTriggerCard key={handler.id} handler={handler} />
						))}
					</Panel>
				</Section>
			</SectionStack>
		</div>
	)
}
function BrazeProfileRefreshCard() {
	const [userId, setUserId] = useState("")
	const mutation = useRefreshBrazeProfile()

	const handleRefresh = () => {
		mutation.mutate(
			{ userId: userId.trim() },
			{
				onSuccess: () => {
					toast.success("Braze profile refreshed", {
						description:
							"Changed attributes were pushed; check the profile in the Braze dev workspace.",
					})
				},
				onError: (error) => {
					toast.error("Braze profile refresh failed", {
						description: error.message,
					})
				},
			},
		)
	}

	return (
		<Section label="Braze profile">
			<SettingRows>
				<SettingRow
					label="Refresh one profile"
					htmlFor="braze-refresh-user"
					help="Recomputes the backend-owned Braze attributes for one user and pushes only the keys that changed, skipping the queue delay."
				>
					<div className="flex items-center gap-2">
						<Input
							id="braze-refresh-user"
							aria-label="User id"
							placeholder="User id (24-char hex)"
							value={userId}
							onChange={(e) => setUserId(e.target.value)}
							className="font-mono text-xs"
						/>
						<Button
							variant="outline"
							onClick={handleRefresh}
							disabled={mutation.isPending || userId.trim().length === 0}
						>
							{mutation.isPending ? "Refreshing…" : "Refresh"}
						</Button>
					</div>
				</SettingRow>
			</SettingRows>
		</Section>
	)
}

function CronTriggerCard({ handler }: { handler: CronHandler }) {
	const mutation = useTriggerCron()

	const handleTrigger = () => {
		mutation.mutate(
			{ handler: handler.id },
			{
				onSuccess: () => {
					toast.success(`${handler.title} completed`, {
						description:
							"The background job ran successfully. Check order statuses to see the results.",
					})
				},
				onError: (error) => {
					toast.error(`${handler.title} failed`, {
						description: error.message,
					})
				},
			},
		)
	}

	return (
		<div className="flex items-start justify-between gap-6 px-4 py-3.5">
			<div className="flex min-w-0 flex-col gap-1">
				<div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
					<span className="font-medium">{handler.title}</span>
					<span className="text-xs text-meta">{handler.schedule}</span>
				</div>
				<p className="text-muted-foreground">{handler.summary}</p>
				<ul className="flex list-disc flex-col gap-0.5 pl-4 text-xs text-meta">
					{handler.actions.map((action) => (
						<li key={action.text}>{action.text}</li>
					))}
				</ul>
			</div>
			<Button
				variant="outline"
				size="sm"
				className="shrink-0"
				onClick={handleTrigger}
				disabled={mutation.isPending}
			>
				{mutation.isPending ? (
					<>
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
						Running…
					</>
				) : (
					<>
						<Play className="h-3.5 w-3.5" />
						Trigger now
					</>
				)}
			</Button>
		</div>
	)
}
